import { useMemo } from "react";
import { Link } from "@heroui/link";
import { Button } from "@heroui/button";
import { Drawer, DrawerContent, DrawerHeader, DrawerBody, DrawerFooter } from "@heroui/drawer";
import { MapPin, Map, MapPinned, Navigation2 } from "lucide-react";
import { useDisclosure } from "@heroui/modal";

const isIOS = () => /iPad|iPhone|iPod/i.test(navigator.userAgent);
const isAndroid = () => /Android/i.test(navigator.userAgent);

function buildMapLinks(record, mode = "search") {
const loc = record?.location || {};
const placeId = loc.place_id || "";
const name = loc.name || "";
const lat = typeof loc.lat === "number" ? loc.lat : null;
const lng = typeof loc.lng === "number" ? loc.lng : null;

const label = encodeURIComponent(name);
const pidParam = placeId ? encodeURIComponent(placeId) : "";

let gmapsWeb, appleWeb, wazeWeb;
if (mode === "directions") {
gmapsWeb = lat != null && lng != null
    ? `https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}${pidParam ? `&destination_place_id=${pidParam}` : ""}`
    : `https://www.google.com/maps/dir/?api=1&destination=${label}${pidParam ? `&destination_place_id=${pidParam}` : ""}`;
appleWeb = lat != null && lng != null
    ? `https://maps.apple.com/?daddr=${lat},${lng}&q=${label}`
    : `https://maps.apple.com/?daddr=${label}`;
wazeWeb = lat != null && lng != null
    ? `https://waze.com/ul?ll=${lat},${lng}&navigate=yes`
    : null;
} else {
// mode === "search"
gmapsWeb = lat != null && lng != null
    ? `https://www.google.com/maps/search/?api=1&query=${lat},${lng}${pidParam ? `&query_place_id=${pidParam}` : ""}`
    : `https://www.google.com/maps/search/?api=1&query=${label}${pidParam ? `&query_place_id=${pidParam}` : ""}`;
appleWeb = lat != null && lng != null
    ? `https://maps.apple.com/?q=${label}&ll=${lat},${lng}`
    : `https://maps.apple.com/?q=${label}`;
wazeWeb = lat != null && lng != null
    ? `https://waze.com/ul?ll=${lat},${lng}&navigate=yes`
    : null;
}

// --- iOS app schemes
let appleApp, gmapsApp, wazeApp;
if (mode === "directions") {
appleApp = lat != null && lng != null
    ? `maps://?daddr=${lat},${lng}&dirflg=d`
    : `maps://?daddr=${label}`;
gmapsApp = lat != null && lng != null
    ? `comgooglemaps://?daddr=${lat},${lng}&directionsmode=driving`
    : `comgooglemaps://?daddr=${label}`;
wazeApp = lat != null && lng != null
    ? `waze://?ll=${lat},${lng}&navigate=yes`
    : null;
} else {
appleApp = lat != null && lng != null
    ? `maps://?q=${label}&ll=${lat},${lng}`
    : `maps://?q=${label}`;
gmapsApp = lat != null && lng != null
    ? `comgooglemaps://?q=${label}&center=${lat},${lng}`
    : `comgooglemaps://?q=${label}`;
wazeApp = lat != null && lng != null
    ? `waze://?ll=${lat},${lng}&navigate=yes`
    : null;
}

// --- Android intents + chooser
let gmapsIntent, wazeIntent, geoChooser;
if (mode === "directions") {
gmapsIntent = lat != null && lng != null
    ? `intent://maps.google.com/maps?daddr=${lat},${lng}(${label})#Intent;scheme=https;package=com.google.android.apps.maps;end`
    : `intent://maps.google.com/maps?daddr=${label}#Intent;scheme=https;package=com.google.android.apps.maps;end`;
wazeIntent = lat != null && lng != null
    ? `intent://?ll=${lat},${lng}&navigate=yes#Intent;scheme=waze;package=com.waze;end`
    : null;
geoChooser = lat != null && lng != null
    ? `geo:${lat},${lng}?q=${lat},${lng}(${label})`
    : null;
} else {
gmapsIntent = lat != null && lng != null
    ? `intent://maps.google.com/maps?q=${lat},${lng}(${label})#Intent;scheme=https;package=com.google.android.apps.maps;end`
    : `intent://maps.google.com/maps?q=${label}#Intent;scheme=https;package=com.google.android.apps.maps;end`;
wazeIntent = lat != null && lng != null
    ? `intent://?ll=${lat},${lng}&navigate=yes#Intent;scheme=waze;package=com.waze;end`
    : null;
geoChooser = lat != null && lng != null
    ? `geo:${lat},${lng}?q=${lat},${lng}(${label})`
    : null;
}

return {
  hasCoords: lat != null && lng != null,
  web: { gmapsWeb, appleWeb, wazeWeb },
  ios: { appleApp, gmapsApp, wazeApp },
  android: { geoChooser, gmapsIntent, wazeIntent },
};
}

function useOpenTarget(record, mode = "search") {
const links = useMemo(() => buildMapLinks(record, mode), [record, mode]);

return (target) => {
let wentBackground = false;
const onVis = () => {
    if (document.visibilityState === "hidden") wentBackground = true;
};
document.addEventListener("visibilitychange", onVis, { once: true });

const startFallback = (url) => {
    const t = setTimeout(() => {
    if (!wentBackground && url) window.location.href = url;
      document.removeEventListener("visibilitychange", onVis);
    }, 900);
    const killer = setTimeout(() => {
      clearTimeout(t);
      document.removeEventListener("visibilitychange", onVis);
    }, 2500);
    return () => { clearTimeout(t); clearTimeout(killer); document.removeEventListener("visibilitychange", onVis); };
};

if (isIOS()) {
    if (target === "apple") {
      const stop = startFallback(links.web.appleWeb);
      window.location.href = links.ios.appleApp;
      return stop;
    }
    if (target === "google") {
      const stop = startFallback(links.web.gmapsWeb);
      window.location.href = links.ios.gmapsApp;
      return stop;
    }
    if (target === "waze") {
      const stop = startFallback(links.web.wazeWeb || links.web.gmapsWeb);
      window.location.href = links.ios.wazeApp || links.web.gmapsWeb;
      return stop;
    }
} else if (isAndroid()) {
    if (target === "chooser-android") {
      window.location.href = links.android.geoChooser || links.web.gmapsWeb;
      return;
    }
    if (target === "google") {
      const stop = startFallback(links.web.gmapsWeb);
      window.location.href = links.android.gmapsIntent;
      return stop;
    }
    if (target === "waze") {
      const stop = startFallback(links.web.wazeWeb || links.web.gmapsWeb);
      window.location.href = links.android.wazeIntent || links.web.gmapsWeb;
      return stop;
    }
    if (target === "apple") {
      window.location.href = links.android.geoChooser || links.web.gmapsWeb;
        return;
    }
}

window.location.href = links.web.gmapsWeb;
};
}

export default function LocationMapsDrawer({ record, mode = "search" }) {
const { isOpen, onOpen, onOpenChange } = useDisclosure();
const openTarget = useOpenTarget(record, mode);

if (!record?.location) return null;

return (
  <>
    <div className="flex gap-3 items-center">
      <div className="flex items-center justify-center w-11 h-11 rounded-md
                      border border-neutral-200 dark:border-white/10
                      bg-white dark:bg-white/5
                      shadow-sm dark:shadow-none">
        <MapPin className="text-neutral-600 dark:text-white/70" size={20} />
      </div>

      <div className="flex flex-col gap-0.5">
        <Link onClick={(e) => { e.preventDefault(); onOpen(); }} href="#" className="group gap-x-0.5 text-medium text-foreground font-medium">
          {record.location.name}
        </Link>

        <p className="text-small text-neutral-600 dark:text-default-500">Tap to open</p>
      </div>
    </div>

    <Drawer isOpen={isOpen} onOpenChange={onOpenChange} placement="bottom" className="z-[70]" classNames={{ backdrop: "bg-black/30" }} backdrop={"blur"}>
      <DrawerContent
        className="
          rounded-t-2xl
          bg-white dark:bg-black/40
          border-t border-neutral-200 dark:border-white/10
          shadow-2xl dark:shadow-[0_-12px_30px_rgba(0,0,0,0.4)]
          backdrop-blur-0 dark:backdrop-blur-xl
        "
      >
        {(close) => (
          <>
            <DrawerHeader className="pb-2 text-neutral-800 dark:text-white">Open with…</DrawerHeader>

            <DrawerBody className="pt-0">
              <div className="flex flex-col gap-2">
                <Button size="md" variant="flat"
                  className="justify-start gap-2 bg-neutral-100 hover:bg-neutral-200 text-neutral-800 dark:bg-default-100/10 dark:text-foreground"
                  startContent={<Map className="w-4 h-4" />}
                  onPress={() => { close(); openTarget(isAndroid() ? "chooser-android" : "apple");}}>
                  Maps
                </Button>

                <Button size="md" variant="flat"
                  className="justify-start gap-2 bg-neutral-100 hover:bg-neutral-200 text-neutral-800 dark:bg-default-100/10 dark:text-foreground"
                  startContent={<MapPinned className="w-4 h-4" />}
                  onPress={() => { close(); openTarget("google");}}>
                  Google Maps
                </Button>

                <Button size="md" variant="flat"
                  className="justify-start gap-2 bg-neutral-100 hover:bg-neutral-200 text-neutral-800 dark:bg-default-100/10 dark:text-foreground"
                  startContent={<Navigation2 className="w-4 h-4" />}
                  onPress={() => { close(); openTarget("waze");}}>
                  Waze
                </Button>
              </div>
            </DrawerBody>

            <DrawerFooter />
          </>
        )}
      </DrawerContent>
    </Drawer>
  </>
);
}